"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateCentroid = exports.isPolygonInsidePolygon = exports.isPointBelongsToSegment = exports.isConvexPolygon = exports.isObjectDescendant = exports.subtractPolygon = exports.getPolygonsIntersections = exports.isPointInsidePolygon = exports.isPointInsideOfConvexHull = exports.getLineSegmentsIntersectionPointXY = exports.sortPointsByPolarCoordinates = exports.buildConvexHullFromPoints = exports.cross2d = exports.getPointWithMinY = exports.projectOnCameraPlane = exports.getBoxVerticesInCameraSpace = exports.getBoundingBoxVertices = exports.getMeshVerticesInCameraSpace = exports.getVertices = exports.calculateSceneBoundingBox = exports.getPolygonSegments = exports.argSort = exports.areVectorsEqualByXY = exports.toVector2Array = exports.toVector2 = void 0;
/* eslint-disable no-restricted-syntax */
const three_1 = require("three");
const utils_3d_1 = require("../../utils/utils.3d");
const Viewer_1 = __importDefault(require("../../Viewer"));
const consts_1 = require("../../consts");
const Polygon_1 = __importDefault(require("./Polygon"));
function toVector2(vector) {
    return new three_1.Vector2(vector.x, vector.y);
}
exports.toVector2 = toVector2;
function toVector2Array(vectors) {
    return vectors.map((vector) => toVector2(vector));
}
exports.toVector2Array = toVector2Array;
function areVectorsEqualByXY(left, right, epsilon = consts_1.consts.STANDARD_PRECISION) {
    return (0, utils_3d_1.areEqual)(left.x, right.x, epsilon) && (0, utils_3d_1.areEqual)(left.y, right.y, epsilon);
}
exports.areVectorsEqualByXY = areVectorsEqualByXY;
function argSort(array, comparator) {
    const comparatorWrapper = (a, b) => comparator(a[0], b[0]);
    return array
        .map((item, i) => [item, i])
        .sort(comparatorWrapper)
        .map((value) => value[1]);
}
exports.argSort = argSort;
function* getPolygonSegments(hull) {
    yield [hull.at(-1), hull[0], hull.length - 1, 0];
    for (let index = 0; index < hull.length - 1; index += 1) {
        yield [hull[index], hull[index + 1], index, index + 1];
    }
}
exports.getPolygonSegments = getPolygonSegments;
function calculateSceneBoundingBox(scene) {
    const { snappingTool } = Viewer_1.default.instance.getDesignProMain();
    const validChildren = scene.children
        .filter(utils_3d_1.isBaseObject3D)
        .filter((object) => { var _a; return object.uuid !== ((_a = snappingTool.getObject()) === null || _a === void 0 ? void 0 : _a.uuid); });
    return validChildren.reduce((box, object) => box.expandByObject(object), new three_1.Box3());
}
exports.calculateSceneBoundingBox = calculateSceneBoundingBox;
function getVertices(geometry, matrix) {
    const positionAttribute = geometry.getAttribute('position');
    const { itemSize } = positionAttribute;
    if (itemSize !== 3) {
        return [];
    }
    const itemsCount = positionAttribute.count;
    const positions = positionAttribute.array;
    const vertices = [];
    for (let i = 0; i < itemsCount * itemSize; i += itemSize) {
        const vertex = new three_1.Vector3(positions[i], positions[i + 1], positions[i + 2]);
        vertices.push(vertex.applyMatrix4(matrix));
    }
    return vertices;
}
exports.getVertices = getVertices;
function getMeshVerticesInCameraSpace(mesh, camera) {
    const fromMeshToCameraMatrix = camera.matrixWorldInverse.clone().multiply(mesh.matrixWorld);
    return getVertices(mesh.geometry, fromMeshToCameraMatrix);
}
exports.getMeshVerticesInCameraSpace = getMeshVerticesInCameraSpace;
function getBoundingBoxVertices(box, matrix = new three_1.Matrix4()) {
    return [
        new three_1.Vector3(box.min.x, box.min.y, box.min.z).applyMatrix4(matrix),
        new three_1.Vector3(box.min.x, box.min.y, box.max.z).applyMatrix4(matrix),
        new three_1.Vector3(box.min.x, box.max.y, box.min.z).applyMatrix4(matrix),
        new three_1.Vector3(box.min.x, box.max.y, box.max.z).applyMatrix4(matrix),
        new three_1.Vector3(box.max.x, box.min.y, box.min.z).applyMatrix4(matrix),
        new three_1.Vector3(box.max.x, box.min.y, box.max.z).applyMatrix4(matrix),
        new three_1.Vector3(box.max.x, box.max.y, box.min.z).applyMatrix4(matrix),
        new three_1.Vector3(box.max.x, box.max.y, box.max.z).applyMatrix4(matrix),
    ];
}
exports.getBoundingBoxVertices = getBoundingBoxVertices;
function getBoxVerticesInCameraSpace(box, camera) {
    return getBoundingBoxVertices(box, camera.matrixWorldInverse);
}
exports.getBoxVerticesInCameraSpace = getBoxVerticesInCameraSpace;
// assume that point is already in camera coordinates
function projectOnCameraPlane(point, zNear) {
    const ratio = -zNear / point.z;
    return new three_1.Vector3(point.x * ratio, point.y * ratio, -zNear);
}
exports.projectOnCameraPlane = projectOnCameraPlane;
function getPointWithMinY(points) {
    let minY = Infinity;
    let minPointIndex = 0;
    points.forEach((point, index) => {
        if (point.y < minY) {
            minPointIndex = index;
            minY = point.y;
        }
    });
    return minPointIndex;
}
exports.getPointWithMinY = getPointWithMinY;
function cross2d(a, b) {
    return a.x * b.y - a.y * b.x;
}
exports.cross2d = cross2d;
// Graham scan implementation
function buildConvexHullFromPoints(points) {
    if (points.length < 3) {
        return [];
    }
    const tempVector = new three_1.Vector3();
    const initialVector = new three_1.Vector3(1, 0);
    const startingPoint = points[getPointWithMinY(points)];
    points.sort((a, b) => {
        // if angle to a < angle (cos a > cos b) to b return negative;
        // if angle to a > angle (cos a < cos b) to b return positive;
        // else if distance from 0 to a < distance b return negative;
        if (a === startingPoint) {
            return -1;
        }
        if (b === startingPoint) {
            return 1;
        }
        const cosineA = tempVector.copy(a).sub(startingPoint).normalize().dot(initialVector);
        const cosineB = tempVector.copy(b).sub(startingPoint).normalize().dot(initialVector);
        if (cosineA > cosineB) {
            return -1;
        }
        if (cosineA < cosineB) {
            return 1;
        }
        return startingPoint.distanceTo(a) - startingPoint.distanceTo(b);
    });
    const hull = [points[0], points[1]];
    for (let i = 2; i < points.length; i += 1) {
        let top = hull.at(-1);
        let nextToTop = hull.at(-2);
        while (hull.length > 1 && cross2d(nextToTop.clone().sub(top), points[i].clone().sub(top)) >= 0) {
            hull.pop();
            top = hull.at(-1);
            nextToTop = hull.at(-2);
        }
        hull.push(points[i]);
    }
    return hull;
}
exports.buildConvexHullFromPoints = buildConvexHullFromPoints;
function sortPointsByPolarCoordinates(points, center) {
    const pointsWithAngles = points.map((point) => {
        const fromCenterToPoint = point.clone().sub(center);
        let angle = Math.atan2(fromCenterToPoint.y, fromCenterToPoint.x);
        angle = angle > 0 ? angle : Math.PI * 2 + angle;
        return [point, angle];
    });
    return [argSort(pointsWithAngles, (a, b) => a[1] - b[1]), pointsWithAngles.map((pair) => pair[1])];
}
exports.sortPointsByPolarCoordinates = sortPointsByPolarCoordinates;
function getLineSegmentsIntersectionPointXY(segment1Start, segment1End, segment2Start, segment2End) {
    // Calculate the direction vectors for each line segment
    const segment1Dir = segment1End.clone().sub(segment1Start);
    const segment2Dir = segment2End.clone().sub(segment2Start);
    // Calculate the determinant of the matrix formed by the direction vectors
    const determinant = segment1Dir.x * segment2Dir.y - segment1Dir.y * segment2Dir.x;
    // If the determinant is zero, the line segments are parallel and do not intersect
    if (determinant === 0) {
        return null;
    }
    // Calculate the vector between the start points of the line segments
    const segmentStartDiff = segment2Start.clone().sub(segment1Start);
    // Calculate the parameter values for the line segments at which they intersect
    const t = (segmentStartDiff.x * segment2Dir.y - segmentStartDiff.y * segment2Dir.x) / determinant;
    const u = (segmentStartDiff.x * segment1Dir.y - segmentStartDiff.y * segment1Dir.x) / determinant;
    // If the parameter values are within the range of 0 to 1, the line segments intersect at the calculated point
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
        return segment1Start.clone().add(segment1Dir.multiplyScalar(t));
    }
    // Otherwise, the line segments do not intersect
    return null;
}
exports.getLineSegmentsIntersectionPointXY = getLineSegmentsIntersectionPointXY;
// assumes that points are in counter-clockwise order
function isPointInsideOfConvexHull(point, hull) {
    // eslint-disable-next-line no-shadow
    let Turn;
    (function (Turn) {
        Turn[Turn["Unknown"] = 0] = "Unknown";
        Turn[Turn["Left"] = 1] = "Left";
        Turn[Turn["Right"] = 2] = "Right";
    })(Turn || (Turn = {}));
    if (hull.length < 3) {
        return false;
    }
    let prevTurn = Turn.Unknown;
    // eslint-disable-next-line no-restricted-syntax
    for (const [begin, end] of getPolygonSegments(hull)) {
        const fromBeginToEnd = end.clone().sub(begin);
        const fromEndToPoint = point.clone().sub(end);
        const crossProduct = cross2d(fromBeginToEnd, fromEndToPoint);
        if ((0, utils_3d_1.areEqual)(crossProduct, 0)) {
            // point lays on the edge line
            // eslint-disable-next-line no-continue
            continue;
        }
        const currentTurn = crossProduct > 0 ? Turn.Left : Turn.Right;
        if (prevTurn === Turn.Unknown) {
            prevTurn = currentTurn;
        }
        else if (prevTurn !== currentTurn) {
            return false;
        }
    }
    return true;
}
exports.isPointInsideOfConvexHull = isPointInsideOfConvexHull;
function isPointInsidePolygon(point, polygon) {
    const yLevel = point.y;
    let isInside = false;
    for (const [begin, end] of getPolygonSegments(polygon)) {
        const hasIntersection = begin.y > yLevel !== end.y > yLevel;
        if (hasIntersection) {
            const intersectionX = begin.x + ((yLevel - begin.y) * (end.x - begin.x)) / (end.y - begin.y);
            if (intersectionX > point.x) {
                isInside = !isInside;
            }
        }
    }
    return isInside;
}
exports.isPointInsidePolygon = isPointInsidePolygon;
function getPolygonsIntersections(polygon1, polygon2) {
    const intersections = [];
    for (const [begin, end, beginIdx, endIdx] of getPolygonSegments(polygon1)) {
        for (const [secondBegin, secondEnd, secondBeginIdx, secondEndIdx] of getPolygonSegments(polygon2)) {
            const intersection = getLineSegmentsIntersectionPointXY(begin, end, secondBegin, secondEnd);
            if (intersection) {
                intersections.push({
                    begin1: beginIdx,
                    end1: endIdx,
                    begin2: secondBeginIdx,
                    end2: secondEndIdx,
                    point: intersection,
                });
            }
        }
    }
    // when intersection point is equal to polygon vertex, intersection point will be added
    // twice - for both edges connected to this polygon vertex
    // to avoid this keep only unique intersection points
    const uniqueIntersections = [];
    intersections.forEach((intersection) => {
        let isUnique = true;
        uniqueIntersections.forEach((uniqueIntersection) => {
            if ((0, utils_3d_1.areVectorsEqual)(intersection.point, uniqueIntersection.point)) {
                isUnique = false;
            }
        });
        if (isUnique) {
            uniqueIntersections.push(intersection);
        }
    });
    return uniqueIntersections;
}
exports.getPolygonsIntersections = getPolygonsIntersections;
function subtractPolygon(polygon1, polygon2) {
    const intersections = getPolygonsIntersections(polygon1, polygon2);
    intersections.sort((a, b) => {
        if (a.begin1 < b.begin1) {
            return -1;
        }
        if (a.begin1 > b.begin1) {
            return 1;
        }
        // a and b have identical edge in first polygon
        const distanceToA = a.point.distanceTo(polygon1[a.begin1]);
        const distanceToB = b.point.distanceTo(polygon1[b.begin1]);
        return distanceToA - distanceToB;
    });
    const polygon1Map = polygon1.map((point, index) => ({ point, index, isIntersection: false }));
    const polygon2Map = polygon2.map((point, index) => ({ point, index, isIntersection: false }));
    intersections.forEach((intersection, intersectionIndex) => {
        const begin = intersection.begin1;
        const end = intersection.end1;
        const distanceToBegin = intersection.point.distanceTo(polygon1[begin]);
        const indexOfBegin = polygon1Map.findIndex((item) => item.index === begin && !item.isIntersection);
        const indexOfEnd = polygon1Map.findIndex((item) => item.index === end && !item.isIntersection);
        let insertionIndex = Polygon_1.default.getNextIndex(indexOfBegin, polygon1Map.length);
        let idx = indexOfBegin;
        while (idx !== indexOfEnd) {
            if (distanceToBegin > polygon1Map[insertionIndex].point.distanceTo(polygon1[begin])) {
                insertionIndex = Polygon_1.default.getNextIndex(insertionIndex, polygon1Map.length);
            }
            idx = Polygon_1.default.getNextIndex(idx, polygon1Map.length);
        }
        polygon1Map.splice(insertionIndex, 0, {
            point: intersection.point,
            index: intersectionIndex,
            isIntersection: true,
        });
    });
    intersections.forEach((intersection, intersectionIndex) => {
        const begin = intersection.begin2;
        const end = intersection.end2;
        const distanceToBegin = intersection.point.distanceTo(polygon2[begin]);
        const indexOfBegin = polygon2Map.findIndex((item) => item.index === begin && !item.isIntersection);
        const indexOfEnd = polygon2Map.findIndex((item) => item.index === end && !item.isIntersection);
        let insertionIndex = Polygon_1.default.getNextIndex(indexOfBegin, polygon2Map.length);
        let idx = indexOfBegin;
        while (idx !== indexOfEnd) {
            if (distanceToBegin > polygon2Map[insertionIndex].point.distanceTo(polygon2[begin])) {
                insertionIndex = Polygon_1.default.getNextIndex(insertionIndex, polygon2Map.length);
            }
            idx = Polygon_1.default.getNextIndex(idx, polygon2Map.length);
        }
        polygon2Map.splice(insertionIndex, 0, {
            point: intersection.point,
            index: intersectionIndex,
            isIntersection: true,
        });
    });
    const polygon1WithIntersections = polygon1Map.map((item) => item.point);
    const polygon2WithIntersections = polygon2Map.map((item) => item.point);
    const intersectionIndicesInP1 = [];
    const intersectionIndicesInP2 = [];
    for (let i = 0; i < intersections.length; i += 1) {
        intersectionIndicesInP1.push(polygon1Map.findIndex((item) => item.isIntersection && item.index === i));
        intersectionIndicesInP2.push(polygon2Map.findIndex((item) => item.isIntersection && item.index === i));
    }
    const processed = new Array(polygon1WithIntersections.length).fill(false);
    const parts = [];
    let startingIndex = polygon1WithIntersections.findIndex((point, index) => !isPointInsideOfConvexHull(point, polygon2) && !processed[index]);
    let currPolygon = polygon1WithIntersections;
    let currIntersectionIndices = intersectionIndicesInP1;
    let currStep = 1;
    const maxIterations = polygon1WithIntersections.length + polygon2WithIntersections.length;
    let iteration = 0; // count iterations for maybe uncovered edge cases
    while (!processed.every(Boolean) && startingIndex !== -1 && iteration < maxIterations) {
        const part = [];
        let currIdx = startingIndex;
        do {
            part.push(currPolygon[currIdx]);
            const currIsP1 = currPolygon === polygon1WithIntersections;
            if (currIsP1) {
                processed[currIdx] = true;
            }
            if (currIntersectionIndices.includes(currIdx)) {
                currPolygon = currIsP1 ? polygon2WithIntersections : polygon1WithIntersections;
                const indexOfIntersection = currIntersectionIndices.indexOf(currIdx);
                currIntersectionIndices = currIsP1 ? intersectionIndicesInP2 : intersectionIndicesInP1;
                if (!currIsP1) {
                    processed[currIntersectionIndices[indexOfIntersection]] = true;
                }
                currStep = currIsP1 ? -1 : 1;
                currIdx = currIntersectionIndices[indexOfIntersection];
            }
            currIdx = Polygon_1.default.getByStep(currIdx, currStep, currPolygon.length);
        } while (currPolygon === polygon2WithIntersections || currIdx !== startingIndex);
        parts.push(part);
        startingIndex = polygon1WithIntersections.findIndex((point, index) => !isPointInsideOfConvexHull(point, polygon2) && !processed[index]);
        iteration += 1;
    }
    return parts;
}
exports.subtractPolygon = subtractPolygon;
function isObjectDescendant(object, potentialParent) {
    // check children of current object
    if (potentialParent.children.includes(object)) {
        return true;
    }
    // check children of all children
    for (let i = 0; i < potentialParent.children.length; i += 1) {
        if (isObjectDescendant(object, potentialParent.children[i])) {
            return true;
        }
    }
    return false;
}
exports.isObjectDescendant = isObjectDescendant;
function isConvexPolygon(polygon) {
    const n = polygon.length;
    let sign = 0;
    for (let i = 0; i < n; i += 1) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % n];
        const p3 = polygon[(i + 2) % n];
        const crossProduct = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x);
        if (crossProduct !== 0) {
            if (sign === 0) {
                sign = Math.sign(crossProduct);
            }
            else if (Math.sign(crossProduct) !== sign) {
                return false;
            }
        }
    }
    return true;
}
exports.isConvexPolygon = isConvexPolygon;
function isPointBelongsToSegment(point, sBegin, sEnd) {
    const fromPointToBegin = sBegin.clone().sub(point);
    const fromPointToEnd = sEnd.clone().sub(point);
    return (0, utils_3d_1.areEqual)(cross2d(fromPointToBegin, fromPointToEnd), 0);
}
exports.isPointBelongsToSegment = isPointBelongsToSegment;
// checks is polygon1 is inside polygon2. Assumes that polygon2 is convex.
function isPolygonInsidePolygon(polygon1, polygon2) {
    // polygon is inside if all it`s points lay inside
    return polygon1.reduce((result, point) => result && isPointInsidePolygon(point, polygon2), true);
}
exports.isPolygonInsidePolygon = isPolygonInsidePolygon;
function calculateCentroid(points) {
    return points.reduce((centroid, point) => centroid.add(point), new three_1.Vector3()).divideScalar(points.length);
}
exports.calculateCentroid = calculateCentroid;
